import { LucideIcon } from "lucide-react";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { Roles } from "@/types/auth/auth";

export interface NavItem {
  title: string;
  path: string;
  icon?: LucideIcon;
  genieIcon?: GenieIconName;
  description?: string;
  children?: NavItem[];
  role?: Roles.Admin | Roles.Business | Roles.Consumer;
  allowedRoles?: string[];
  isHeader?: boolean;
  divider?: boolean;
  isAdmin?: boolean;
  disabled?: boolean;
  comingSoon?: boolean;
  badge?: string;
  externalUrl?: string; // opens in a new tab instead of using React Router
}

export const genieHubNavItems: NavItem[] = [
  {
    title: "Knowledge Base",
    path: "/genie-hub/knowledge-base",
    genieIcon: "brain",
    description: "What your genies know — feed them documents, transcripts, and answers",
    isHeader: true,
  },
  {
    title: "Activation",
    path: "/genie-hub/activation",
    genieIcon: "qr-code",
    description: "Share your genies with the people who need them",
    isHeader: true,
  },
  {
    title: "Genie Operations",
    path: "/genie-hub",
    genieIcon: "genie-lamp",
    description: "Build and manage your genies",
    isHeader: true,
    children: [
      { title: "My Genies", path: "/genie-hub/my-genies" },
      { title: "Genie Pages", path: "/genie-hub/genie-pages" },
      { title: "Genie Portals", path: "/genie-hub/portals" },
      { title: "Genie Trust Center", path: "/genie-hub/brand-integrity" },
    ],
  },
  {
    title: "Insights",
    path: "/genie-hub/insights",
    genieIcon: "trending-up",
    description: "What's working across your genies",
    isHeader: true,
  },
  {
    title: "System & Settings",
    path: "/settings",
    genieIcon: "settings",
    description: "Connect and configure",
    isHeader: true,
    children: [
      { title: "Email Templates", path: "/genie-hub/email-templates" },
      { title: "Account Branding", path: "/genie-hub/account-branding" },
      { title: "Voice Playground", path: "/genie-hub/voice-playground" },
      { title: "Developers", path: "/developers", externalUrl: "https://helpgenie.ai/docs" },
    ],
  },
  {
    title: "Learn",
    path: "/learn",
    genieIcon: "book-open",
    description: "Master Help Genie with guides, videos, and your Tutor Genie",
    isHeader: true,
    children: [
      { title: "Start Here", path: "/learn?tab=start" },
      { title: "Library", path: "/learn?tab=library" },
      { title: "Playbooks", path: "/learn?tab=playbooks" },
      { title: "Genie Tutor", path: "/learn/tutor" },
    ],
  },
];

// =====================================================================
// Help Genie Suite — elevated upgrade-tier section
//
// Rendered separately from `genieHubNavItems` so the sidebar can give it
// distinct visual treatment (gradient border, brand-colored icons, external-
// link arrows). These are companion products that open in their
// own tabs — not internal pages — so the visual lane should make that obvious.
// =====================================================================

export interface SuiteApp {
  title: string;
  productWord: string;
  genieIcon: GenieIconName;
  /** Brand accent for this Suite app */
  color: string;
  path: string;
  description: string;
  /** External URL for SSO redirect. When absent, clicking navigates to `path` internally. */
  externalUrl?: string;
  /** Path on the external app that accepts SSO token query params (e.g. /sso). If set, SSO token is appended here instead of externalUrl. */
  ssoEntryPath?: string;
}

export const suiteApps: SuiteApp[] = [
  {
    title: "Mail",
    productWord: "Mail",
    genieIcon: "mail",
    color: "#4E9CFF",
    path: "/genie-hub/channels/mail",
    externalUrl: "https://email.helpgenie.ai/login",
    description: "Answers your support email from your Genie's knowledge base, and escalates the rest to you.",
  },
  {
    title: "Sync",
    productWord: "Sync",
    genieIcon: "sync",
    color: "#FF4DB8",
    path: "/genie-hub/channels/sync",
    externalUrl: "https://sync.helpgenie.ai/login",
    description: "Connect HubSpot, Slack, and other tools so Genies can act.",
  },
  {
    title: "Outbound",
    productWord: "Outbound",
    genieIcon: "phone-outgoing",
    color: "#FFBB33",
    path: "/genie-hub/channels/outbound",
    externalUrl: "https://outbound.helpgenie.ai/login",
    description: "Place real calls and run campaigns end-to-end.",
  },
];

export const suiteLandingPath = "/genie-hub/suite";

// Admin Tab Types - Vertical icon tabs (VS Code style)
// Order: Users, Genies, Logs, Monitoring, Operations, Extra
export type AdminTabKey = "users" | "genies" | "logs" | "monitoring" | "operations" | "website";

export interface AdminTab {
  key: AdminTabKey;
  label: string;
  description: string;
  genieIcon: GenieIconName;
  items: NavItem[];
}

// Tab-based admin navigation configuration (VS Code style vertical tabs)
export const adminTabConfig: Record<AdminTabKey, AdminTab> = {
  users: {
    key: "users",
    label: "Users",
    description: "User management",
    genieIcon: "users",
    items: [
      {
        title: "Users",
        path: "/admin/users",
        isAdmin: true,
      },
      {
        title: "Phone Numbers",
        path: "/admin/phone-numbers",
        isAdmin: true,
      },
      {
        title: "Custom Slack Apps",
        path: "/admin/slack-apps",
        isAdmin: true,
      },
      {
        title: "Recent Conversations",
        path: "/admin/recent-conversations",
        isAdmin: true,
      },
    ],
  },
  genies: {
    key: "genies",
    label: "Genies",
    description: "Manage genies and intelligence",
    genieIcon: "genie-lamp",
    items: [
      {
        title: "Genies",
        path: "/admin/voice-agents",
        isAdmin: true,
      },
      {
        title: "Demo Genies",
        path: "/admin/demo-genies",
        isAdmin: true,
      },
      {
        title: "Inactive Genies",
        path: "/admin/inactive-genies",
        isAdmin: true,
      },
      {
        title: "Knowledge Base",
        path: "/admin/knowledge-base",
        isAdmin: true,
      },
      {
        title: "Agent Mailboxes",
        path: "/admin/agent-mailboxes",
        isAdmin: true,
      },
    ],
  },
  logs: {
    key: "logs",
    label: "Logs",
    description: "Activity and system logs",
    genieIcon: "activity",
    items: [
      {
        title: "Brain Chat Logs",
        path: "/admin/brain-chat-logs",
        isAdmin: true,
      },
      {
        title: "Conversation Logs",
        path: "/admin/conversation-logs",
        isAdmin: true,
      },
      {
        title: "Edge Function Logs",
        path: "/admin/edge-function-logs",
        isAdmin: true,
      },
      {
        title: "Email Logs",
        path: "/admin/email-logs",
        isAdmin: true,
      },
      {
        title: "Genie Creation Logs",
        path: "/admin/agent-creation-logs",
        isAdmin: true,
      },
      {
        title: "Genie Meetings",
        path: "/admin/genie-meetings",
        isAdmin: true,
      },
    ],
  },
  monitoring: {
    key: "monitoring",
    label: "Monitoring",
    description: "System health and usage",
    genieIcon: "gauge",
    items: [
      {
        title: "Bulk Update Jobs",
        path: "/admin/bulk-update-jobs",
        isAdmin: true,
      },
      {
        title: "Suite Visits",
        path: "/admin/suite-visit-logs",
        isAdmin: true,
      },
      {
        title: "Page Views",
        path: "/admin/page-views",
        isAdmin: true,
      },
      {
        title: "AI Usage",
        path: "/admin/ai-usage",
        isAdmin: true,
      },
    ],
  },
  operations: {
    key: "operations",
    label: "Operations",
    description: "Internal operations and tools",
    genieIcon: "wrench",
    items: [
      {
        title: "Developer Docs",
        path: "/admin/dev-docs",
        isAdmin: true,
      },
      {
        title: "System Emails",
        path: "/admin/system-emails",
        isAdmin: true,
      },
      {
        title: "Widget Tester",
        path: "/admin/widget-tester",
        isAdmin: true,
      },
      {
        title: "Operations Hub",
        path: "/operations/hub",
        externalUrl: "https://operations.helpgenie.ai/",
        isAdmin: true,
      },
    ],
  },
  website: {
    key: "website",
    label: "Extra",
    description: "General data, media, and marketplace",
    genieIcon: "database",
    items: [
      {
        title: "General Data Library",
        path: "/admin/general-library",
        isAdmin: true,
      },
      {
        title: "Image Generator",
        path: "/admin/image-generator",
        isAdmin: true,
      },
      {
        title: "Marketplace",
        path: "/admin/marketplace",
        isAdmin: true,
      },
      {
        title: "Preselected Voices",
        path: "/admin/preselected-voices",
        isAdmin: true,
      },
      {
        title: "Pronunciation Vaults",
        path: "/admin/pronunciation-vaults",
        isAdmin: true,
      },
    ],
  },
};

// Helper to get all admin tabs as an array
export const adminTabs: AdminTab[] = Object.values(adminTabConfig);

// Helper to get nav items for a specific tab
export const getAdminNavItemsForTab = (tabKey: AdminTabKey): NavItem[] => {
  return adminTabConfig[tabKey]?.items || [];
};

// Legacy export - combines all admin items (for backward compatibility)
export const adminNavItems: NavItem[] = Object.values(adminTabConfig).flatMap((tab) => tab.items);

export const mainNavItems: NavItem[] = [
  {
    title: "Inbox",
    path: "/dashboard",
    genieIcon: "chat-check",
  },
];
